import { useMemo } from 'react';
import { PHASE_LABELS, PHASE_ORDER } from '../lib/procedureMeta';
import type { ProcedurePhase } from '../lib/types';

/**
 * Frise horizontale des phases de procédure pénale belge.
 *
 * **Modes** :
 * - `lawyer` (défaut) : libellés techniques (« Règlement de la procédure
 *   (chambre du conseil) »)
 * - `client` : libellés simplifiés (« Décision sur la suite : procès ou
 *   non-lieu ») + tooltip explicatif sur les étapes futures pour que
 *   le client visualise « ce qui va arriver »
 *
 * **Affichage des phases** (Sprint 3+, 2026-05-21) :
 * Avant : seules les phases passées + courante + obligatoires étaient
 * visibles, les futures non-visitées étaient masquées. Maxim a demandé
 * que le client puisse voir « les prochaines étapes utiles » →
 * on montre désormais TOUTES les phases canoniques (de Préliminaire à
 * Clos), avec un état visuel distinct pour les futures (à venir).
 *
 * Cohérence avec la séquence canonique vérifiée sur le corpus pénal
 * (Michiels/Falque, *Principes de procédure pénale*, 2024) :
 * Préliminaire → Instruction → Règlement (CC) → Fond → Appel →
 * Cassation → Exécution → Clos.
 */

const PHASES_REQUIRED: Set<ProcedurePhase> = new Set(['preliminaire']);

/** Tooltips pour les phases futures en mode client (déjuridicisé). */
const PHASE_HINT_CLIENT: Record<ProcedurePhase, string> = {
  preliminaire:
    'Le procureur enquête : police, auditions, perquisitions. Vous pouvez être convoqué pour une audition (avec votre avocat).',
  instruction:
    "Un juge d'instruction prend la main pour les enquêtes plus lourdes. Il peut décider de mesures (perquisition, mandat d'arrêt).",
  reglement:
    "La chambre du conseil décide : renvoi devant le tribunal, non-lieu, ou complément d'instruction. Vous serez entendu(e) avec votre avocat.",
  fond_correctionnel:
    'Procès devant le tribunal correctionnel : preuves, plaidoiries, jugement (peine ou acquittement).',
  fond_assises:
    "Procès devant la cour d'assises : jury populaire pour les crimes les plus graves (assassinat, attentat, etc.).",
  appel_correctionnel:
    "Recours devant la cour d'appel si vous (ou le parquet) n'êtes pas d'accord avec le jugement. Réexamen complet du dossier.",
  cassation:
    'Contrôle de la légalité du jugement (pas du fond) par la Cour de cassation. Si erreur de droit : renvoi devant une autre juridiction.',
  execution: "Exécution de la peine (amende, prison, sursis, travail d'intérêt général…).",
  clos: 'Le dossier est définitivement clôturé.',
};

type Props = {
  currentPhase: ProcedurePhase;
  /** Sous-état courant (libellé court). */
  currentSubState?: string | null | undefined;
  /** Phases qui ont été traversées (au moins un événement) — détermine
   *  les pastilles « passée » + les phases optionnelles affichées. */
  visitedPhases: ReadonlySet<ProcedurePhase>;
  /** Si true, variante assises remplace fond_correctionnel. */
  varianteAssises?: boolean;
  /** Mode d'affichage (libellés avocat vs client). */
  mode?: 'lawyer' | 'client';
  onPick?: ((phase: ProcedurePhase) => void) | undefined;
  /** Si true (défaut), affiche TOUTES les phases à venir jusqu'à `clos`.
   *  Si false, ne montre que passées + courante + visitées (legacy). */
  showFutureSteps?: boolean;
};

export default function TimelineStepper({
  currentPhase,
  currentSubState,
  visitedPhases,
  varianteAssises = false,
  mode = 'lawyer',
  onPick,
  showFutureSteps = true,
}: Props) {
  const visible = useMemo(() => {
    const currentIdx = PHASE_ORDER.indexOf(currentPhase);
    return PHASE_ORDER.filter((p, idx) => {
      // Variantes mutually exclusive : fond correctionnel XOR assises
      if (p === 'fond_correctionnel' && varianteAssises) return false;
      if (p === 'fond_assises' && !varianteAssises) return false;
      if (PHASES_REQUIRED.has(p)) return true;
      if (p === currentPhase) return true;
      if (visitedPhases.has(p)) return true;
      // Nouveau : phases futures non-visitées affichées si showFutureSteps.
      // « clos » est tronqué si la phase courante est antérieure pour éviter
      // de promettre la clôture (cassation reste affichée).
      if (showFutureSteps && idx > currentIdx) return true;
      return false;
    });
  }, [currentPhase, visitedPhases, varianteAssises, showFutureSteps]);

  const currentIdx = visible.indexOf(currentPhase);

  return (
    <div className="space-y-2">
      <ol className="flex items-center gap-1 overflow-x-auto px-1 py-2">
        {visible.map((phase, idx) => {
          const isCurrent = phase === currentPhase;
          const isPast = idx < currentIdx;
          const isFuture = idx > currentIdx;
          const showConnector = idx < visible.length - 1;
          const label = mode === 'client' ? PHASE_LABELS[phase].client : PHASE_LABELS[phase].lawyer;
          const tooltip =
            mode === 'client' && isFuture ? `${label} — ${PHASE_HINT_CLIENT[phase]}` : label;
          return (
            <li key={phase} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onPick?.(phase)}
                disabled={!onPick}
                className={`group flex flex-col items-center gap-1 rounded-md px-1.5 py-0.5 transition ${
                  onPick ? 'cursor-pointer hover:bg-accent-light' : 'cursor-default'
                }`}
                title={tooltip}
              >
                <span
                  className={[
                    'flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-medium transition',
                    isPast && 'bg-accent text-paper-elevated',
                    isCurrent && 'border-2 border-accent bg-accent-light text-accent-text',
                    // Futures : pointillés + italique pour signaler « à venir »
                    isFuture &&
                      'border border-dashed border-rule-strong bg-paper-elevated text-text-tertiary',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isPast ? '✓' : idx + 1}
                </span>
                <span
                  className={[
                    'whitespace-nowrap text-[10.5px]',
                    isCurrent && 'font-medium text-text-primary',
                    isPast && 'text-text-secondary',
                    isFuture && 'italic text-text-tertiary',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {label}
                </span>
              </button>
              {showConnector && (
                <span
                  className={[
                    'h-px w-6 shrink-0',
                    isPast
                      ? 'bg-accent'
                      : isFuture
                        ? 'border-t border-dashed border-rule'
                        : 'bg-rule',
                  ].join(' ')}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
      {currentSubState && (
        <p className="px-1 text-[11.5px] text-text-secondary">
          Sous-état :{' '}
          <span className="font-mono font-medium text-text-primary">{currentSubState}</span>
        </p>
      )}
      {mode === 'client' && (
        <p className="px-1 text-[10.5px] italic text-text-tertiary">
          Étapes en pointillés = à venir. Survolez chaque pastille pour voir ce que cela implique
          pour vous.
        </p>
      )}
    </div>
  );
}
